import { Bed, CircleHelp, Hammer, SquareSlash } from "lucide-react"
import { cn } from "@/lib/utils"
import type { TachoCardActivity } from "@/lib/aiWidgetTachoParser"
import {
  formatMin,
  formatWhen,
  staleMinutes,
  type Light,
} from "./tachoCardFormat"
import { useTachoCardStrings } from "./tachoCardStrings"

export const LIGHT_DOT: Record<Light, string> = {
  red: "bg-red-500",
  amber: "bg-amber-400",
  green: "bg-green-500",
  off: "bg-muted-foreground/30",
}

export const LIGHT_TEXT: Record<Light, string> = {
  red: "text-red-600",
  amber: "text-amber-600",
  green: "text-foreground",
  off: "text-muted-foreground",
}

function SteeringWheel({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={className}
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="2" />
      <path d="M12 14v7M10 12H3M14 12h7" />
    </svg>
  )
}

function CrossedHammers({ className }: { className?: string }) {
  return (
    <span className={cn("relative inline-block", className)}>
      <Hammer className="absolute inset-0 h-full w-full" />
      <Hammer className="absolute inset-0 h-full w-full -scale-x-100" />
    </span>
  )
}

export const ACTIVITY_ICON: Record<
  TachoCardActivity,
  React.ComponentType<{ className?: string }>
> = {
  driving: SteeringWheel,
  work: CrossedHammers,
  rest: Bed,
  availability: SquareSlash,
  unknown: CircleHelp,
}

export function TachoSourceFooter({
  sourceAt,
  now,
}: {
  sourceAt: string | null
  now: number
}) {
  const t = useTachoCardStrings()
  if (!sourceAt) {
    return (
      <div className="mt-4 border-t border-border pt-2 text-xs text-muted-foreground">
        {t.noSourceTime}
      </div>
    )
  }
  const age = staleMinutes(sourceAt, now)
  const stale = age > 60
  return (
    <div
      className={cn(
        "mt-4 border-t border-border pt-2 text-xs",
        stale ? "text-amber-600" : "text-muted-foreground",
      )}
    >
      {t.source(formatWhen(sourceAt, t.weekdays))}
      {stale && t.stale(formatMin(age))}
    </div>
  )
}
